'use client';

import { useRef } from 'react';
import Image from 'next/image';
import { resolveImageUrl } from '@/lib/images';
import type { ListingFormImage } from '@/lib/listing-form';
import {
  MAX_LISTING_IMAGES,
  createLocalImages,
  ensureCover,
  moveImage,
  removeImageAt,
  setCoverAt,
} from '@/lib/listing-form';
import { listingFormStyles as s } from './listingFormStyles';
import { theme } from '@/lib/uiTheme';

type Props = {
  images: ListingFormImage[];
  onChange: (images: ListingFormImage[]) => void;
  disabled?: boolean;
  uploadProgress?: { current: number; total: number; fileName: string } | null;
};

function displaySrc(img: ListingFormImage): string | null {
  if (img.previewUrl) return img.previewUrl;
  if (img.storageKey) return resolveImageUrl(img.storageKey);
  return null;
}

export default function ListingImageManager({
  images,
  onChange,
  disabled,
  uploadProgress,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  function addFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const remaining = MAX_LISTING_IMAGES - images.length;
    if (remaining <= 0) return;
    const files = Array.from(fileList)
      .filter((f) => f.type.startsWith('image/'))
      .slice(0, remaining);
    if (files.length === 0) return;
    const next = ensureCover([...images, ...createLocalImages(files)]);
    onChange(next);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <button
          type="button"
          disabled={disabled || images.length >= MAX_LISTING_IMAGES}
          onClick={() => inputRef.current?.click()}
          style={{
            ...s.btnPrimary,
            ...(disabled || images.length >= MAX_LISTING_IMAGES ? s.btnDisabled : null),
          }}
        >
          Add photos
        </button>
        <span style={{ fontSize: 13, color: theme.colors.inkSubtle }}>
          {images.length} / {MAX_LISTING_IMAGES} photos
        </span>
        <input
          ref={inputRef}
          id="listing-form-images"
          name="listing-form-images"
          type="file"
          accept="image/*"
          multiple
          disabled={disabled}
          onChange={(e) => addFiles(e.target.files)}
          style={{ display: 'none' }}
        />
      </div>

      {uploadProgress ? (
        <p style={{ marginBottom: 12, fontSize: 14, color: theme.colors.inkMuted }}>
          Uploading photo {uploadProgress.current} of {uploadProgress.total}
          {uploadProgress.fileName ? `: ${uploadProgress.fileName}` : ''}
        </p>
      ) : null}

      {images.length === 0 ? (
        <p style={s.sectionHint}>No photos yet. You can publish without photos and add them later.</p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 140px), 1fr))',
            gap: 12,
          }}
        >
          {images.map((img, index) => {
            const src = displaySrc(img);
            return (
              <li
                key={img.id}
                style={{
                  border: `1px solid ${img.isCover ? theme.colors.primary : theme.colors.border}`,
                  borderRadius: theme.radii.lg,
                  overflow: 'hidden',
                  background: theme.colors.surface,
                }}
              >
                <div style={{ position: 'relative', aspectRatio: '4 / 3', background: theme.colors.surfaceSubtle }}>
                  {src ? (
                    <Image
                      src={src}
                      alt={`Listing photo ${index + 1}`}
                      fill
                      unoptimized={src.startsWith('blob:')}
                      style={{ objectFit: 'cover' }}
                      sizes="160px"
                    />
                  ) : (
                    <div style={{ display: 'grid', placeItems: 'center', height: '100%', color: theme.colors.inkSubtle }}>
                      No preview
                    </div>
                  )}
                  {img.isCover ? (
                    <span
                      style={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        background: theme.colors.primary,
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 999,
                      }}
                    >
                      Cover
                    </span>
                  ) : null}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 8 }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      type="button"
                      aria-label={`Move photo ${index + 1} left`}
                      disabled={disabled || index === 0}
                      onClick={() => onChange(moveImage(images, index, -1))}
                      style={{ ...s.btnGhost, padding: '6px 8px', flex: 1, fontSize: 12 }}
                    >
                      Left
                    </button>
                    <button
                      type="button"
                      aria-label={`Move photo ${index + 1} right`}
                      disabled={disabled || index === images.length - 1}
                      onClick={() => onChange(moveImage(images, index, 1))}
                      style={{ ...s.btnGhost, padding: '6px 8px', flex: 1, fontSize: 12 }}
                    >
                      Right
                    </button>
                  </div>
                  <button
                    type="button"
                    disabled={disabled || img.isCover}
                    onClick={() => onChange(setCoverAt(images, index))}
                    style={{ ...s.btnGhost, padding: '6px 8px', fontSize: 12 }}
                  >
                    {img.isCover ? 'Current cover' : 'Set as cover'}
                  </button>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(removeImageAt(images, index))}
                    style={{
                      ...s.btnGhost,
                      padding: '6px 8px',
                      fontSize: 12,
                      color: theme.colors.danger,
                      borderColor: '#fecaca',
                    }}
                  >
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
